import React, { useState, useEffect, useRef } from 'react';
import {
  Brain,
  MessageCircle,
  User,
  Bot,
  Sparkles,
  ArrowRight,
  CheckCircle,
  Lightbulb,
  Target,
  TrendingUp
} from 'lucide-react';
import { assessmentConversation } from '../mockData/showcaseMockData';

const AssessmentDemo = ({ data }) => {
  const [conversationStage, setConversationStage] = useState(0);
  const [showInsights, setShowInsights] = useState(false);
  const [typingMessage, setTypingMessage] = useState('');
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef(null);

  // Intersection observer for triggering animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isInView) {
          setIsInView(true);
          // Start conversation animation
          setTimeout(() => startConversation(), 500);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isInView]);

  const startConversation = () => {
    // Progressive conversation display
    const timings = [1000, 3000, 5000, 7000];
    timings.forEach((timing, index) => {
      setTimeout(() => {
        setConversationStage(index + 1);
        if (index === timings.length - 1) {
          setTimeout(() => setShowInsights(true), 1500);
        }
      }, timing);
    });
  };

  // Typewriter effect for user messages
  const typewriterEffect = (text, callback) => {
    let index = 0;
    setTypingMessage('');
    const timer = setInterval(() => {
      if (index < text.length) {
        setTypingMessage(prev => prev + text[index]);
        index++;
      } else {
        clearInterval(timer);
        callback && callback();
      }
    }, 30);
  };

  const conversation = [
    {
      type: 'bot',
      message: "Tell me about something you know a lot about that most people don't."
    },
    {
      type: 'user',
      message: "I know way too much about irrigation systems because I spent last summer helping my neighbor's farm survive the drought. I ended up building them a solar-powered robot that monitors soil moisture.",
      highlight: ['irrigation systems', 'solar-powered robot', 'drought']
    },
    {
      type: 'bot',
      message: "That's fascinating! You combined environmental awareness with technical innovation. Tell me more about a time you stood up for something even when it was difficult."
    },
    {
      type: 'user',
      message: "When our school wanted to cancel the recycling program to save money, I presented a data analysis showing we could actually make money by optimizing the system. I got in trouble for going over the principal's head to the school board, but it worked.",
      highlight: ['data analysis', 'recycling program', 'school board']
    }
  ];

  return (
    <div ref={sectionRef} className="max-w-6xl mx-auto px-4">
      {/* Section Header */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-violet-50 rounded-full mb-4">
          <Brain className="w-4 h-4 text-violet-600" />
          <span className="text-sm font-medium text-violet-700">
            Strategic Assessment
          </span>
        </div>
        <h2 className="text-4xl font-bold text-slate-900 mb-4">
          Discover Your Authentic Story
        </h2>
        <p className="text-xl text-slate-600 max-w-3xl mx-auto">
          Our AI has conversations, not surveys. Watch how it uncovers Alex's hidden strengths
          and unique perspectives that admissions officers want to see.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Left: Conversation Interface */}
        <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 bg-gradient-to-r from-violet-600 to-indigo-600">
            <h3 className="font-semibold text-white flex items-center gap-2">
              <MessageCircle className="w-5 h-5" />
              Strategic Discovery Conversation
            </h3>
          </div>

          <div className="p-6 space-y-4 h-[500px] overflow-y-auto">
            {conversation.slice(0, conversationStage).map((msg, index) => (
              <div
                key={index}
                className={`flex gap-3 ${msg.type === 'user' ? 'flex-row-reverse' : ''}
                  animate-fadeIn`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0
                  ${msg.type === 'user' ? 'bg-azure-100' : 'bg-violet-100'}`}
                >
                  {msg.type === 'user' ? (
                    <User className="w-4 h-4 text-azure-600" />
                  ) : (
                    <Bot className="w-4 h-4 text-violet-600" />
                  )}
                </div>

                <div className={`flex-1 ${msg.type === 'user' ? 'text-right' : ''}`}>
                  <div className={`inline-block px-4 py-2 rounded-lg max-w-[80%]
                    ${msg.type === 'user'
                      ? 'bg-azure-600 text-white text-left'
                      : 'bg-slate-100 text-slate-900'}`}
                  >
                    {msg.highlight ? (
                      <span>
                        {msg.message.split(' ').map((word, i) => {
                          const isHighlight = msg.highlight.some(h =>
                            word.toLowerCase().includes(h.toLowerCase()));
                          return (
                            <span key={i}>
                              {isHighlight ? (
                                <span className="bg-azure-500/30 px-1 rounded">
                                  {word}
                                </span>
                              ) : word}
                              {' '}
                            </span>
                          );
                        })}
                      </span>
                    ) : msg.message}
                  </div>
                </div>
              </div>
            ))}

            {/* Typing indicator */}
            {conversationStage > 0 && conversationStage < 4 && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center">
                  <Bot className="w-4 h-4 text-violet-600" />
                </div>
                <div className="px-4 py-2 bg-slate-100 rounded-lg">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" />
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce delay-100" />
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce delay-200" />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right: AI Analysis & Insights */}
        <div className="space-y-4">
          {/* Pattern Recognition */}
          <div className={`bg-white rounded-xl shadow-lg border border-slate-200 p-6
            transition-all duration-700 ${showInsights ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <div className="flex items-center gap-2 mb-4">
              <Target className="w-5 h-5 text-indigo-600" />
              <h3 className="font-semibold text-slate-900">Pattern Recognition</h3>
            </div>

            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                </div>
                <div>
                  <div className="font-medium text-slate-900">Technical Innovation + Environmental Impact</div>
                  <div className="text-sm text-slate-600 mt-1">
                    Unique combination that sets Alex apart from typical STEM applicants
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                </div>
                <div>
                  <div className="font-medium text-slate-900">Data-Driven Advocacy</div>
                  <div className="text-sm text-slate-600 mt-1">
                    Uses analytical skills to drive social change, not just academic success
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                </div>
                <div>
                  <div className="font-medium text-slate-900">Principled Risk-Taking</div>
                  <div className="text-sm text-slate-600 mt-1">
                    Willing to challenge authority when backed by data and conviction
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Generated Insights */}
          <div className={`bg-gradient-to-br from-violet-50 to-indigo-50 rounded-xl border border-violet-200 p-6
            transition-all duration-700 delay-200 ${showInsights ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-violet-600" />
              <h3 className="font-semibold text-slate-900">Your Unique Story</h3>
            </div>

            <div className="space-y-4">
              <div>
                <div className="text-sm font-medium text-violet-900 mb-1">Core Theme</div>
                <div className="text-slate-700">
                  "The Environmental Tech Innovator" - You don't just build technology,
                  you build solutions for real-world environmental challenges.
                </div>
              </div>

              <div>
                <div className="text-sm font-medium text-violet-900 mb-1">Hidden Strength</div>
                <div className="text-slate-700">
                  Your ability to see systems (irrigation, recycling, coding) and optimize them
                  shows exceptional analytical thinking applied to practical problems.
                </div>
              </div>

              <div>
                <div className="text-sm font-medium text-violet-900 mb-1">Essay Angle</div>
                <div className="text-slate-700">
                  Focus on the moment you realized technology could solve environmental problems -
                  the drought/robot story perfectly captures your unique perspective.
                </div>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-violet-200">
              <div className="flex items-center gap-2 text-sm text-violet-700">
                <Lightbulb className="w-4 h-4" />
                <span className="font-medium">
                  This profile would stand out at Brown's environmental studies program
                </span>
              </div>
            </div>
          </div>

          {/* Action Items */}
          <div className={`bg-white rounded-xl shadow-lg border border-slate-200 p-6
            transition-all duration-700 delay-400 ${showInsights ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
              <h3 className="font-semibold text-slate-900">Strategic Next Steps</h3>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <ArrowRight className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-700">
                  Emphasize solar robot project in all STEM school applications
                </span>
              </div>
              <div className="flex items-center gap-2">
                <ArrowRight className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-700">
                  Request recommendation from neighbor whose farm you helped
                </span>
              </div>
              <div className="flex items-center gap-2">
                <ArrowRight className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-700">
                  Frame recycling program story as leadership essay
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentDemo;